import { disableStickyPromptCardWithCooldown } from 'actions/StickyPromptCardActions'
import { searchPaneInteractEventName } from 'components/Search/utils'
import config from 'constants/config'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import useToggle from 'hooks/useToggle'
import { useCallback, useEffect } from 'react'
import useCarHireReengagementSelectionLogic from './useCarHireReengagementSelectionLogic'
import CarHireReengagementCard from 'components/CarHire/CarHireReengagementCard'

interface Props {
  hotLead?: boolean
  stickyTop?: number
  carHire?: boolean
}

const ENTRY_DELAY_MS = 2000

// disable all cards for some time after dismissal
const GLOBAL_COOLDOWN_AFTER_DISMISS_MS = config.STICKY_PROMPT_CARD_DISMISS_COOLDOWN_MS ?? 0

function PromptCardSelector({
  carHire: carHireReengagementCardEnabledProp = false,
  stickyTop,
}: Props) {
  // Tracks whether any card has been dismissed from this component instance
  const { value: cardDismissedFromHere, on: setCardDismissedFromHere } = useToggle(false)

  const { value: enabled, off: disable } = useToggle(true)

  // Hide the card if the user uses the search bar
  useEffect(() => {
    const handler = () => {
      disable()
    }
    document.addEventListener(searchPaneInteractEventName, handler)
    document.addEventListener('app-snackbar', handler)
    return () => {
      document.removeEventListener(searchPaneInteractEventName, handler)
      document.removeEventListener('app-snackbar', handler)
    }
  }, [disable])

  const carHireReengagementCardEnabled = carHireReengagementCardEnabledProp && enabled

  const {
    carHireReengagementView,
    setCarHireCardDismissed,
    canShowCarHireCard,
    isCarHireDataReady,
  } = useCarHireReengagementSelectionLogic({ carHireReengagementCardEnabled })

  // For each card that's enabled, make sure we have the required data
  const readyToDecideCard = isCarHireDataReady || !carHireReengagementCardEnabled

  // Initial delay before rendering the card
  const { value: isDelayed, off: finishDelay } = useToggle(true)
  useEffect(() => {
    const timeout = setTimeout(finishDelay, ENTRY_DELAY_MS)
    return () => clearTimeout(timeout)
  }, [finishDelay])

  const isStickyCardEnabled = useAppSelector(state => state.stickyPromptCard.isEnabled)

  const canShowAnyCard = readyToDecideCard && !cardDismissedFromHere && !isDelayed && isStickyCardEnabled
  const shouldShowCarHireCard = (
    canShowAnyCard &&
    canShowCarHireCard
  )
  const dispatch = useAppDispatch()

  const handleDismiss = useCallback(() => {
    dispatch(disableStickyPromptCardWithCooldown(GLOBAL_COOLDOWN_AFTER_DISMISS_MS))

    setCardDismissedFromHere()
  }, [dispatch, setCardDismissedFromHere])

  const onCloseCarHireCard = useCallback(() => {
    setCarHireCardDismissed()
    handleDismiss()
  }, [handleDismiss, setCarHireCardDismissed])

  return <>
    {!!carHireReengagementView.offerSummary && <CarHireReengagementCard
      carHireReengagementView={carHireReengagementView}
      offerSummary={carHireReengagementView.offerSummary}
      onClose={onCloseCarHireCard}
      stickyTop={stickyTop}
      show={shouldShowCarHireCard}
    />}
  </>
}

export default PromptCardSelector
